import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination } from '@mui/material';
import { RestartAlt as RestartAltIcon } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useChartConfig } from './ChartConfig';

const SpillsCharts = ({ 
  spillsData, 
  onSortSpillDetails, 
  spillDetailsSortField, 
  spillDetailsSortDirection,
  onResetSpillDetails,
  hiddenSpillLines,
  setHiddenSpillLines,
  resetHiddenSpillLines,
  hiddenSpillVolumeLines,
  setHiddenSpillVolumeLines,
  resetHiddenSpillVolumeLines
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const chartConfig = useChartConfig();

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };





  return (
    <Box sx={{ mt: 2 }}>
      {/* Chart 1: Spills Occurrence */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Spills by Cause vs Time
          </Typography>
          <Tooltip title="Reset Hidden Lines" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setHiddenSpillLines(new Set())}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <ResponsiveContainer {...chartConfig.containerProps}>
          <BarChart 
            data={(() => {
              // Group data by month and cause of loss
              const monthlyData = {};
              
              spillsData.forEach(record => {
                const dateColumn = record['Date of Loss'] !== undefined ? 'Date of Loss' :
                                 record.date_of_loss !== undefined ? 'date_of_loss' : null;
                
                if (dateColumn && record[dateColumn]) {
                  const date = new Date(record[dateColumn]);
                  if (!isNaN(date.getTime())) {
                    const monthKey = date.toISOString().substring(0, 7); // YYYY-MM format
                    
                    if (!monthlyData[monthKey]) {
                      monthlyData[monthKey] = {
                        month: monthKey,
                        totalSpills: 0
                      };
                    }
                    
                    monthlyData[monthKey].totalSpills++;
                    
                    // Add cause of loss data
                    const causeColumn = record['Cause of Loss'] !== undefined ? 'Cause of Loss' :
                                      record.cause_of_loss !== undefined ? 'cause_of_loss' : null;
                    
                    if (causeColumn && record[causeColumn]) {
                      const cause = record[causeColumn].toString().trim();
                      if (!monthlyData[monthKey][cause]) {
                        monthlyData[monthKey][cause] = 0;
                      }
                      monthlyData[monthKey][cause]++;
                    }
                  }
                }
              });
              
              // Convert to array and sort by month
              return Object.values(monthlyData)
                .sort((a, b) => a.month.localeCompare(b.month));
            })()}
            margin={chartConfig.margins}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis />
            <RechartsTooltip 
              formatter={(value, name) => {
                if (name === 'totalSpills') {
                  return [`${value.toLocaleString()}`, 'Total Spills'];
                }
                return [`${value.toLocaleString()}`, name.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())];
              }}
              labelFormatter={(label) => `Month: ${label}`}
            />
            <Legend 
              onClick={(entry) => {
                const lineType = entry.dataKey;
                setHiddenSpillLines(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(lineType)) {
                    newSet.delete(lineType);
                  } else {
                    newSet.add(lineType);
                  }
                  return newSet;
                });
              }}
            />
            {(() => {
              // Get all unique causes from the data
              const allCauses = new Set();
              spillsData.forEach(record => {
                const causeColumn = record['Cause of Loss'] !== undefined ? 'Cause of Loss' :
                                  record.cause_of_loss !== undefined ? 'cause_of_loss' : null;
                if (causeColumn && record[causeColumn]) {
                  allCauses.add(record[causeColumn].toString().trim());
                }
              });
              
              // Color scheme for causes
              const causeColors = [
                '#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', 
                '#00bcd4', '#8bc34a', '#ffc107', '#795548', '#607d8b'
              ];
              
              return Array.from(allCauses).map((cause, index) => (
                <Bar
                  key={cause}
                  dataKey={cause}
                  stackId="a"
                  fill={causeColors[index % causeColors.length]}
                  name={cause.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                  hide={hiddenSpillLines.has(cause)}
                />
              ));
            })()}
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Chart 2: Spill Volume */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Spill Volume Trends
          </Typography>
          <Tooltip title="Reset Hidden Lines" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setHiddenSpillVolumeLines(new Set())}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <ResponsiveContainer {...chartConfig.containerProps}>
          <ComposedChart 
            data={(() => {
              // Group data by month and calculate volumes
              const monthlyData = {};
              
              spillsData.forEach(record => {
                const dateColumn = record['Date of Loss'] !== undefined ? 'Date of Loss' :
                                 record.date_of_loss !== undefined ? 'date_of_loss' : null;
                
                if (dateColumn && record[dateColumn]) {
                  const date = new Date(record[dateColumn]);
                  if (!isNaN(date.getTime())) {
                    const monthKey = date.toISOString().substring(0, 7); // YYYY-MM format
                    
                    if (!monthlyData[monthKey]) {
                      monthlyData[monthKey] = {
                        month: monthKey,
                        grossLoss: 0,
                        netLoss: 0,
                        recovered: 0
                      };
                    }
                    
                    // Add volumes
                    const grossLossColumn = record['Gross Loss'] !== undefined ? 'Gross Loss' :
                                           record.gross_loss !== undefined ? 'gross_loss' : null;
                    const netLossColumn = record['Net Loss'] !== undefined ? 'Net Loss' :
                                          record.net_loss !== undefined ? 'net_loss' : null;
                    const recoveredColumn = record['Recovered'] !== undefined ? 'Recovered' :
                                            record.recovered !== undefined ? 'recovered' : null;
                    
                    if (grossLossColumn) {
                      monthlyData[monthKey].grossLoss += parseFloat(record[grossLossColumn]) || 0;
                    }
                    if (netLossColumn) {
                      monthlyData[monthKey].netLoss += parseFloat(record[netLossColumn]) || 0;
                    }
                    if (recoveredColumn) {
                      monthlyData[monthKey].recovered += parseFloat(record[recoveredColumn]) || 0;
                    }
                  }
                }
              });
              
              // Convert to array and sort by month
              return Object.values(monthlyData)
                .sort((a, b) => a.month.localeCompare(b.month));
            })()}
            margin={chartConfig.margins}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis yAxisId="left" />
            <RechartsTooltip 
              formatter={(value, name) => {
                if (name === 'Gross Loss') {
                  return [`${value.toLocaleString()} bbl`, 'Gross'];
                } else if (name === 'Net Loss') {
                  return [`${value.toLocaleString()} bbl`, 'Net'];
                } else if (name === 'Recovered') {
                  return [`${value.toLocaleString()} bbl`, 'Recovered'];
                }
                return [value, name];
              }}
              labelFormatter={(label) => `Month: ${label}`}
            />
            <Legend 
              onClick={(entry) => {
                const lineType = entry.dataKey;
                setHiddenSpillVolumeLines(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(lineType)) {
                    newSet.delete(lineType);
                  } else {
                    newSet.add(lineType);
                  }
                  return newSet;
                });
              }}
            />
            <Bar 
              yAxisId="left"
              dataKey="netLoss" 
              stackId="a"
              fill="#f44336" 
              name="Net Loss"
              hide={hiddenSpillVolumeLines.has('netLoss')}
            />
            <Bar 
              yAxisId="left"
              dataKey="recovered" 
              stackId="a"
              fill="#4caf50" 
              name="Recovered"
              hide={hiddenSpillVolumeLines.has('recovered')}
            />
            <Line 
              yAxisId="left"
              type="monotone" 
              dataKey="grossLoss" 
              stroke="#ff9800" 
              strokeWidth={2}
              name="Gross Loss"
              dot={{ fill: '#ff9800', strokeWidth: 2, r: 4 }}
              hide={hiddenSpillVolumeLines.has('grossLoss')}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Paper>

      {/* Spill Details */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, border: 1, borderColor: 'divider' }} data-table-title="Spill Details" data-table-type="main">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Spill Details
          </Typography>
          <Tooltip title="Reset Table Sort" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                onResetSpillDetails();
                resetHiddenSpillLines();
                resetHiddenSpillVolumeLines();
              }}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortSpillDetails('lease_name')}
                >
                  Lease Name
                  {spillDetailsSortField === 'lease_name' && (
                    <span style={{ marginLeft: 8 }}>
                      {spillDetailsSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortSpillDetails('date_of_loss')}
                >
                  Date of Loss
                  {spillDetailsSortField === 'date_of_loss' && (
                    <span style={{ marginLeft: 8 }}>
                      {spillDetailsSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortSpillDetails('gross_loss')}
                >
                  Gross Loss
                  {spillDetailsSortField === 'gross_loss' && (
                    <span style={{ marginLeft: 8 }}>
                      {spillDetailsSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortSpillDetails('cause_of_loss')}
                >
                  Cause of Loss
                  {spillDetailsSortField === 'cause_of_loss' && (
                    <span style={{ marginLeft: 8 }}>
                      {spillDetailsSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortSpillDetails('water_impact')}
                >
                  Water Impact
                  {spillDetailsSortField === 'water_impact' && (
                    <span style={{ marginLeft: 8 }}>
                      {spillDetailsSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortSpillDetails('remarks')}
                >
                  Remarks
                  {spillDetailsSortField === 'remarks' && (
                    <span style={{ marginLeft: 8 }}>
                      {spillDetailsSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                // Sort the data based on current sort field and direction
                const sortedData = [...spillsData].sort((a, b) => {
                  let aValue, bValue;
                  
                  switch (spillDetailsSortField) {
                    case 'lease_name':
                      aValue = a['Lease Name'] || a.lease_name || '';
                      bValue = b['Lease Name'] || b.lease_name || '';
                      break;
                    case 'date_of_loss':
                      aValue = new Date(a['Date of Loss'] || a.date_of_loss || 0);
                      bValue = new Date(b['Date of Loss'] || b.date_of_loss || 0);
                      break;
                    case 'gross_loss':
                      aValue = parseFloat(a['Gross Loss'] || a.gross_loss || 0);
                      bValue = parseFloat(b['Gross Loss'] || b.gross_loss || 0);
                      break;
                    case 'cause_of_loss':
                      aValue = a['Cause of Loss'] || a.cause_of_loss || '';
                      bValue = b['Cause of Loss'] || b.cause_of_loss || '';
                      break;
                    case 'water_impact':
                      aValue = a['Water Impact'] || a.water_impact || '';
                      bValue = b['Water Impact'] || b.water_impact || '';
                      break;
                    case 'remarks':
                      aValue = a['Remarks'] || a.remarks || '';
                      bValue = b['Remarks'] || b.remarks || '';
                      break;
                    default:
                      return 0;
                  }
                  
                  if (aValue < bValue) return spillDetailsSortDirection === 'asc' ? -1 : 1;
                  if (aValue > bValue) return spillDetailsSortDirection === 'asc' ? 1 : -1;
                  return 0;
                });
                
                // Apply pagination
                const startIndex = page * rowsPerPage;
                const endIndex = startIndex + rowsPerPage;
                const paginatedData = sortedData.slice(startIndex, endIndex);
                
                return paginatedData.map((record, index) => (
                  <TableRow key={startIndex + index}>
                    <TableCell>{record['Lease Name'] || record.lease_name || ''}</TableCell>
                    <TableCell>
                      {record['Date of Loss'] || record.date_of_loss || ''}
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        const grossLoss = record['Gross Loss'] || record.gross_loss;
                        return grossLoss ? parseFloat(grossLoss).toLocaleString() : '0';
                      })()}
                    </TableCell>
                    <TableCell>{record['Cause of Loss'] || record.cause_of_loss || ''}</TableCell>
                    <TableCell>{record['Water Impact'] || record.water_impact || ''}</TableCell>
                    <TableCell>{record['Remarks'] || record.remarks || ''}</TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={spillsData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
          sx={{
            '& .MuiTablePagination-toolbar': {
              justifyContent: 'center',
            },
            '& .MuiTablePagination-spacer': {
              display: 'none',
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default SpillsCharts;